import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import { verifyMagicLink } from "@/store/auth";
import { navigate, replaceRoute } from "@/routes/router";
import { extractAuthError } from "./errors";
import "../../components/ui/button.ts";
import "../../components/ui/spinner.ts";
import "../../components/ui/iris-icon.ts";
import "../../layouts/auth-layout.ts";

/**
 * Landing page for an emailed sign-in link.
 *
 * Firebase redirects here with `oobCode` (the one-time code) and the `email`
 * we attached to the continue URL. The email has to travel in the URL because
 * this is a backend-driven flow: there is no Firebase SDK on the page keeping
 * the pending address in browser storage.
 */
@localized()
@customElement("iris-magic-link-callback-page")
export class IrisMagicLinkCallbackPage extends LitElement {
  static styles = css`
    :host {
      display: contents;
    }
    .panel {
      width: 100%;
      max-width: 400px;
      padding: var(--space-8) var(--space-7);
      border: 1px solid var(--border);
      border-radius: var(--radius-xl);
      background: var(--card);
      box-shadow:
        0 1px 2px rgba(15, 23, 42, 0.04),
        0 24px 48px -24px rgba(15, 23, 42, 0.18);
      text-align: center;
      animation: card-in var(--dur-glacial) var(--ease-1) both;
    }
    @keyframes card-in {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.985);
      }
      to {
        opacity: 1;
        transform: none;
      }
    }
    .status-icon {
      display: grid;
      place-items: center;
      width: 64px;
      height: 64px;
      margin: 0 auto var(--space-5);
      border-radius: var(--radius-full);
      background: color-mix(in srgb, var(--primary) 12%, transparent);
      color: var(--primary);
    }
    .status-icon.error {
      background: color-mix(in srgb, var(--destructive) 12%, transparent);
      color: var(--destructive);
    }
    h1 {
      margin: 0 0 var(--space-1-5);
      font-size: var(--text-2xl);
      font-weight: 600;
      letter-spacing: -0.01em;
      color: var(--foreground);
    }
    p {
      margin: 0 0 var(--space-5);
      font-size: var(--text-sm);
      line-height: 1.55;
      color: var(--muted-foreground);
    }
  `;

  @state()
  private error = "";

  connectedCallback(): void {
    super.connectedCallback();
    void this.#redeem();
  }

  async #redeem(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get("oobCode") ?? "";
    // Firebase's action handler sometimes forwards our continue URL as a
    // nested `continueUrl` param instead of merging its query into the
    // redirect, so the address can arrive either way. sessionStorage is the
    // last resort, set when the link was requested.
    const email = params.get("email") ??
      new URLSearchParams(
        (params.get("continueUrl") ?? "").split("?")[1] ?? "",
      ).get("email") ??
      window.sessionStorage.getItem("pending_magic_link_email") ??
      "";
    const inviteToken = params.get("invite_token") ??
      new URLSearchParams(
        (params.get("continueUrl") ?? "").split("?")[1] ?? "",
      ).get("invite_token") ??
      "";

    if (!oobCode || !email) {
      this.error = msg("This sign-in link is incomplete. Request a new one.");
      return;
    }

    try {
      await verifyMagicLink(email, oobCode, inviteToken);
      window.sessionStorage.removeItem("pending_magic_link_email");
      // Replace the entry so the one-time code is not left in history, where
      // a back navigation would retry an already-spent code. replaceRoute also
      // moves the route signal; plain replaceState would leave the shell on
      // /auth/callback and render not-found under the new URL.
      replaceRoute("/sprint-dashboard");
    } catch (err) {
      this.error = extractAuthError(err);
    }
  }

  protected render() {
    return html`
      <iris-auth-layout>
        <section class="panel" aria-live="polite">
          ${this.error
            ? html`
              <div class="status-icon error">
                <iris-icon name="alert-circle" size="28"></iris-icon>
              </div>
              <h1>${msg("Sign-in failed")}</h1>
              <p>${this.error}</p>
              <iris-button fluid @click=${() => navigate("/login")}>
                ${msg("Back to sign in")}
              </iris-button>
            `
            : html`
              <div class="status-icon">
                <iris-spinner></iris-spinner>
              </div>
              <h1>${msg("Signing you in")}</h1>
              <p>${msg("Verifying your link…")}</p>
            `}
        </section>
      </iris-auth-layout>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "iris-magic-link-callback-page": IrisMagicLinkCallbackPage;
  }
}
